import { styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { Loading } from 'shell/components';

import useFormValidation from 'services/hooks/useFormValidation';
import { Autocomplete, Box, Button, Dropdown, IconButton, Stack, Text, TextInput } from 'shell/components';
import { INSTITUTIONS, MAJORS, SEMESTER_OPTIONS } from 'utils/constants';

const LIST_OF_SCHOOLS = [...INSTITUTIONS, { name: 'Other' }].map(({ name }) => ({ key: name, value: name }));
const LIST_OF_SEMESTERS = SEMESTER_OPTIONS.map(({ title, value }) => ({ key: value, value: title }));
const LIST_OF_MAJORS = MAJORS.map(({ name }) => ({ key: name, value: name }));

const CourseDetailsSettings = ({ device, course, updateCourseSettings, openDeleteCourseDialog }) => {
    const defaultFormValues = {
        school: LIST_OF_SCHOOLS.find(({ value }) => value === course?.school) || { key: 'Other', value: 'Other' },
        schoolOther: course?.school,
        faculty: course?.faculty,
        name: course?.name,
        section: course?.section,
        semester: LIST_OF_SEMESTERS.find(({ value }) => value === course?.semester) || null,
        field_of_study: LIST_OF_MAJORS.find(({ value }) => value === course?.field_of_study) || null,
    };

    const [form, setForm] = useState(defaultFormValues);
    const [isDirty, setIsDirty] = useState(false);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    const getErrorMessage = (key, value, form) => {
        if (['schoolOther', 'faculty', 'name', 'section'].includes(key)) value = value?.trim();
        switch (key) {
            case 'schoolOther':
                return form.school.value === 'Other' && !value ? 'Please enter your school' : '';
            case 'faculty':
                return value ? '' : 'Please enter your department';
            case 'name':
                return value ? '' : 'Please enter your course';
            case 'section':
                return value ? '' : 'Please enter your section';
            case 'semester':
                return value ? '' : 'Please select your semester';
            case 'field_of_study':
                return value ? '' : 'Please select your field of study';
            default:
                return '';
        }
    };

    const handleSettingDropDown = ({ key }) => {
        switch (key) {
            case 'delete-course': {
                return openDeleteCourseDialog(course);
            }
            default:
                return;
        }
    };

    const { errors, validate, validateAll } = useFormValidation(form, getErrorMessage);

    const handleChange = (key, value) => {
        setForm((current) => ({ ...current, [key]: value }));
        setIsDirty(true);
        if (hasClickedSubmit || errors[key]) {
            validate(key, value);
        }
    };

    const submit = () => {
        if (!hasClickedSubmit) setHasClickedSubmit(true);
        if (!validateAll()) return;
        updateCourseSettings({
            faculty: form.faculty,
            field_of_study: form.field_of_study.value,
            name: form.name,
            school: form.school.value === 'Other' ? form.schoolOther : form.school.value,
            section: form.section,
            semester: form.semester.value,
        });
        setIsDirty(false);
    };

    const reset = () => {
        setForm(defaultFormValues);
        setHasClickedSubmit(false);
        setIsDirty(false);
    };

    useEffect(() => {
        if (course) setForm(defaultFormValues);
    }, [course]);

    useEffect(() => {
        if (!isDirty) validateAll();
    }, [isDirty]);

    return !course ? (
        <Loading style={{ marginTop: '30vh', height: 'fit-content' }} />
    ) : (
        <CustomStack device={device}>
            <Stack alignItems={'center'} direction={'row'} spacing={3}>
                <Text variant={'h6'} weight={'semibold'}>
                    Course Details
                </Text>
                <Dropdown
                    id={'course-setting-dropdown'}
                    options={[{ key: 'delete-course', value: 'Delete Course' }]}
                    rootComponent={
                        <IconButton
                            aria-label={'more options'}
                            kind={'moreHorizontal'}
                            size={'medium'}
                            variant={'square'}
                        />
                    }
                    onSelect={handleSettingDropDown}
                />
            </Stack>
            <InputsContainer device={device}>
                <Autocomplete
                    disableClearable
                    error={Boolean(errors.school)}
                    helperText={errors.school}
                    label={'School'}
                    options={LIST_OF_SCHOOLS}
                    required
                    value={form.school}
                    virtualized
                    onChange={(_, value) => handleChange('school', value)}
                />
                {form.school.value === 'Other' ? (
                    <TextInput
                        sx={{ gridColumn: '1 / 3' }}
                        error={Boolean(errors.schoolOther)}
                        label={'Please Specify:'}
                        helperText={errors.schoolOther}
                        required
                        value={form.schoolOther}
                        onChange={(e) => handleChange('schoolOther', e.target.value)}
                    />
                ) : null}
                <TextInput
                    error={Boolean(errors.faculty)}
                    helperText={errors.faculty}
                    label={'Department'}
                    required
                    value={form.faculty}
                    onChange={(e) => handleChange('faculty', e.target.value)}
                />
                <TextInput
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    label={'Course Name'}
                    required
                    value={form.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                />
                <TextInput
                    error={Boolean(errors.section)}
                    helperText={errors.section}
                    label={'Section'}
                    required
                    value={form.section}
                    onChange={(e) => handleChange('section', e.target.value)}
                />
                <Autocomplete
                    disableClearable
                    error={Boolean(errors.semester)}
                    helperText={errors.semester}
                    label={'Semester'}
                    options={LIST_OF_SEMESTERS}
                    required
                    value={form.semester}
                    onChange={(_, value) => handleChange('semester', value)}
                />
                <Autocomplete
                    disableClearable
                    error={Boolean(errors.field_of_study)}
                    helperText={errors.field_of_study}
                    label={'Field of Study'}
                    options={LIST_OF_MAJORS}
                    required
                    value={form.field_of_study}
                    onChange={(_, value) => handleChange('field_of_study', value)}
                />
            </InputsContainer>
            <ActionsContainer
                device={device}
                direction={'row-reverse'}
                justifyContent={device === 'mobile' ? 'space-between' : 'initial'}
                spacing={4}
            >
                <Button disabled={!isDirty} onClick={submit}>
                    Update
                </Button>
                {isDirty && (
                    <Button variant={'secondary'} onClick={reset}>
                        Cancel
                    </Button>
                )}
            </ActionsContainer>
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ device, theme }) => {
    return {
        paddingTop: theme.spacing(4),
        ...(device === 'mobile' && {
            paddingBottom: theme.spacing(8),
        }),
    };
});

const InputsContainer = styled(Box)(({ device, theme }) => {
    return {
        display: 'grid',
        margin: theme.spacing(4, 0),
        ...(device === 'mobile' && {
            gridTemplateColumns: 'auto',
            gridGap: theme.spacing(4),
        }),
        ...(device === 'tablet' && {
            gridTemplateColumns: '1fr 1fr',
            gridGap: theme.spacing(4),
            '& > div:first-child': {
                gridColumn: '1 / 3',
            },
        }),
        ...(device === 'desktop' && {
            gridTemplateColumns: '1fr 1fr',
            gridColumnGap: theme.spacing(8),
            gridRowGap: theme.spacing(6),
            width: 'fit-content',
            '& > div:first-child': {
                gridColumn: '1 / 3',
            },
            '& > div:not(:nth-child(1), :nth-child(2))': {
                width: '300px',
            },
        }),
    };
});

const ActionsContainer = styled(Stack)(({ device, theme }) => {
    return {
        ...(device === 'mobile' && {
            marginTop: theme.spacing(2),
            width: '100%',
        }),
        ...(device === 'tablet' && {
            width: '100%',
        }),
        ...(device === 'desktop' && {
            width: '632px',
        }),
    };
});

export default CourseDetailsSettings;
